/**
 * Positions between polls.
 *
 * Owner, 2026-09-11: *"Låt webbläsare beräkna position mellan hämtningar."* The
 * world is fetched every three seconds, and a marching symbol used to jump to
 * wherever the server had it. The report already carries the route, the share
 * travelled and the game time left, so the browser walks the same route in
 * between.
 *
 * This mirrors `units::position_along` on the server — progress in game time,
 * position by DISTANCE along the route, haversine on the same radius — so the
 * next poll lands where the symbol already is instead of pulling it back.
 */

const EARTH_M = 6371008.8;

/** Metres between two [lon, lat] points. */
function metres(a: [number, number], b: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLon = (b[0] - a[0]) * rad;
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.sqrt(h));
}

/**
 * The [lon, lat] point a `fraction` of the way along `path`, measured by
 * distance. By vertex index a formation would crawl through dense bends and
 * jump across straight stretches — the same reason the server walks it this way.
 */
export function pointAlong(path: [number, number][], fraction: number): [number, number] | null {
  if (!path || path.length === 0) return null;
  const f = Math.min(1, Math.max(0, fraction));
  if (path.length === 1 || f === 0) return path[0];
  if (f === 1) return path[path.length - 1];

  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < path.length; i++) {
    const d = metres(path[i - 1], path[i]);
    seg.push(d);
    total += d;
  }
  if (total <= 0) return path[path.length - 1];

  const target = total * f;
  let walked = 0;
  for (let i = 1; i < path.length; i++) {
    const d = seg[i - 1];
    if (walked + d >= target) {
      const t = d > 0 ? (target - walked) / d : 0;
      const a = path[i - 1];
      const b = path[i];
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    walked += d;
  }
  return path[path.length - 1];
}

/** The parts of a formation the walk needs — all of them in the world report. */
export interface Gliding {
  lat: number;
  lon: number;
  /** 0..1 of the march done at the poll, null when holding still. */
  progress: number | null;
  /** Game seconds left at the poll. */
  eta_s: number | null;
  /** Game seconds before travel starts; included in eta_s. */
  starts_in_s: number | null;
  path: [number, number][] | null;
}

/**
 * [lat, lon] of a formation `wallS` wall seconds after its poll, with the
 * theatre running at `rate` game seconds per wall second (0 when paused).
 */
export function glide(u: Gliding, wallS: number, rate: number): [number, number] {
  if (u.progress === null || u.eta_s === null || !u.path || u.path.length < 2) return [u.lat, u.lon];
  const advance = Math.max(0, wallS) * Math.max(0, rate);
  if (advance === 0) return [u.lat, u.lon];
  const startsIn = Math.max(0, u.starts_in_s ?? 0);
  if (advance <= startsIn) return [u.lat, u.lon];
  const travelAdvance = advance - startsIn;

  let fraction = 1;
  if (u.progress < 1 && u.eta_s > 0) {
    // progress = elapsed / duration and eta = duration − elapsed, as the server
    // defines them, so the whole march lasts eta / (1 − progress).
    const travelEta = Math.max(0, u.eta_s - startsIn);
    const duration = travelEta / (1 - u.progress);
    fraction = duration > 0 ? Math.min(1, u.progress + travelAdvance / duration) : 1;
  }
  const p = pointAlong(u.path, fraction);
  return p ? [p[1], p[0]] : [u.lat, u.lon];
}
